import { ReactNode, useEffect } from "react";

import isEmpty from "lodash/isEmpty";
import { useNavigate } from "react-router-dom";

import {
  GetAccountResponse,
  useGetAccount,
} from "apps-common/hooks/useGetAccount";
import { MembershipType, AllowedUsers } from "apps-common/types";
import { throwError } from "apps-common/utils/errorHandler";
import { isStrictNever } from "apps-common/utils/isStrictNever";
import { Loader } from "ui";

import { routes } from "../routes";
import {
  isActiveSubscription,
  isLegacyMember,
  isPendingSubscription,
} from "../utils/member";

interface UserAccessCheckProps {
  allowedUsers: AllowedUsers[];
  children: ReactNode;
}

const getUserType = (
  membershipType: MembershipType,
  currentSubscription:
    | GetAccountResponse["account"]["currentSubscription"]
    | undefined,
  paymentMethods: GetAccountResponse["account"]["paymentMethods"] | undefined,
): AllowedUsers => {
  if (isLegacyMember(membershipType) || !currentSubscription) {
    return AllowedUsers.LEGACY;
  }

  const subscriptionState = currentSubscription.subscriptionState;

  switch (membershipType) {
    case MembershipType.LIFETIME: {
      return AllowedUsers.LIFETIME;
    }

    case MembershipType.B2C: {
      if (isPendingSubscription(subscriptionState)) {
        return AllowedUsers.PENDING_B2C;
      }
      if (isEmpty(paymentMethods)) {
        return AllowedUsers.B2C_NO_PAYMENT_METHOD;
      }
      if (isActiveSubscription(subscriptionState)) {
        return AllowedUsers.ACTIVE_B2C_WITH_PAYMENT_METHOD;
      }
      return AllowedUsers.INACTIVE_B2C_WITH_PAYMENT_METHOD;
    }

    case MembershipType.B2B: {
      if (isActiveSubscription(subscriptionState)) {
        return AllowedUsers.ACTIVE_B2B;
      }
      return AllowedUsers.INACTIVE_B2B;
    }

    case MembershipType.UNFINISHED:
    case MembershipType.UNVERIFIED: {
      throw new Error("Unfinished and unverified users are not allowed");
    }

    default: {
      return isStrictNever(membershipType);
    }
  }
};

const isAllowedUser = (
  membershipType: MembershipType,
  allowedUsers: AllowedUsers[],
  currentSubscription:
    | GetAccountResponse["account"]["currentSubscription"]
    | undefined,
  paymentMethods: GetAccountResponse["account"]["paymentMethods"] | undefined,
) => {
  try {
    const userType = getUserType(
      membershipType,
      currentSubscription,
      paymentMethods,
    );

    if (allowedUsers.includes(AllowedUsers.ALL)) return true;

    return allowedUsers.includes(userType);
  } catch (error) {
    return false;
  }
};

const useNavigateToIndex = (allowedUsers: AllowedUsers[]) => {
  const navigate = useNavigate();

  const { data: accountData } = useGetAccount();
  const currentSubscription = accountData?.account.currentSubscription;
  const membershipType = accountData?.account.membershipType;
  const paymentMethods = accountData?.account.paymentMethods;

  useEffect(() => {
    if (
      membershipType &&
      !isAllowedUser(
        membershipType,
        allowedUsers,
        currentSubscription,
        paymentMethods,
      )
    ) {
      navigate(routes.index);
    }
  }, [
    membershipType,
    allowedUsers,
    navigate,
    currentSubscription,
    paymentMethods,
  ]);
};

export const UserAccessCheck = ({
  children,
  allowedUsers,
}: UserAccessCheckProps) => {
  const { data: accountData, isFetching, isError, error } = useGetAccount();
  const { currentSubscription, membershipType, paymentMethods } =
    accountData?.account ?? {};

  useNavigateToIndex(allowedUsers);

  if (isFetching) {
    return <Loader />;
  }

  if (isError) {
    throw throwError("errorOnGetAccount", error);
  }

  if (!membershipType) {
    // Account not found. The customer should create the account and membership through the signup flow.
    throw throwError("errorOnB2CNoSubNoAccessOrNullMembershipType");
  }

  if (membershipType === MembershipType.UNFINISHED) {
    // The customer is not a legacy hardware user but they don't have subscription either (not even expired one). They should be asked to finish the signup flow.
    throw throwError("errorOnB2CNoSubNoAccessOrNullMembershipType");
  }

  if (membershipType === MembershipType.UNVERIFIED) {
    // The customer's account is unverified. They should be asked to verify it by requesting a new password through forgot password feature.
    throw throwError("errorOnUnverifiedAccount");
  }

  if (
    !isAllowedUser(
      membershipType,
      allowedUsers,
      currentSubscription,
      paymentMethods,
    )
  ) {
    // Return null to prevent flash of content before redirect
    return null;
  }

  return children;
};
